import { useEffect } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useCallModerationState } from "@/zustand/CallModerationState";
import { ORION_MODERATION_TOOLBAR_BUTTON_ID } from "./jitsiToolbar";

// Re-exported so importers of this ID have a hook-local handle; source of truth is `jitsiToolbar.ts`.
export { ORION_MODERATION_TOOLBAR_BUTTON_ID };

/**
 * Payload of Jitsi's `toolbarButtonClicked` event. The `key` field carries the button id
 * (confirmed against the deployed bundle, build v=9268); `id`/`buttonId` are read defensively.
 * Mirrors `useOrionChatButtonEvent`.
 */
type ToolbarButtonClickedEvent = {
  key?: string;
  id?: string;
  buttonId?: string;
};

/**
 * Wires the Jitsi iframe's `toolbarButtonClicked` event for the "Moderation" custom toolbar button
 * to the Orion moderation panel.
 *
 * ROLE GATING (defence in depth): `customToolbarButtons` entries are global (there's no per-role
 * flag), so the button is rendered for EVERYONE in the initial config; `useModerationButtonVisibility`
 * then removes it at runtime for a confirmed non-moderator, but only once the local role is known —
 * so a non-moderator can still see it during the role-unknown window (or indefinitely, if the deployed
 * build's `overwriteConfig` ever no-ops). We therefore ALSO gate the ACTION here: the panel only
 * toggles when the local participant is a moderator. For non-moderators the click is a no-op (and
 * `ModerationPanel` also self-gates, rendering nothing for non-moderators), so a stray click can never
 * surface the moderation UI even while the button is still visible.
 *
 * Reads `useCallModerationState.getState()` at call time (same pattern as `useModerationEvents`) so
 * the listener never captures stale state/action references across renders.
 */
export default function useModerationButtonEvent(
  apiRef: IJitsiMeetExternalApi | null,
): void {
  useEffect(() => {
    if (!apiRef) {
      return;
    }

    function handleToolbarButtonClicked(payload?: ToolbarButtonClickedEvent) {
      const clickedId = payload?.key ?? payload?.id ?? payload?.buttonId;
      if (clickedId !== ORION_MODERATION_TOOLBAR_BUTTON_ID) {
        return;
      }
      const state = useCallModerationState.getState();
      // Non-moderators get a no-op — the button is global but the panel is moderator-only.
      if (!state.localIsModerator) {
        return;
      }
      state.toggleModerationPanel();
    }

    apiRef.on("toolbarButtonClicked", handleToolbarButtonClicked);

    return () => {
      apiRef.removeListener("toolbarButtonClicked", handleToolbarButtonClicked);
    };
  }, [apiRef]);
}
